import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { GALLERY } from "@/data";
import { Reveal, MaskLine } from "@/components/Reveal";

export const Gallery = () => {
  const [open, setOpen] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 lg:py-36 bg-[#1C130E]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12 lg:mb-16">
          <div className="max-w-2xl">
            <Reveal>
              <p className="eyebrow mb-5 text-[#D4A359]">Morning Rush</p>
            </Reveal>
            <h2 className="font-display font-medium leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#FDFBF7]">
              <MaskLine>Every day starts</MaskLine>
              <MaskLine delay={0.12}>
                in the <span className="italic text-[#C85A32]">dark.</span>
              </MaskLine>
            </h2>
          </div>
          <Reveal>
            <p className="text-base leading-relaxed text-[#FDFBF7]/60 max-w-sm">
              From the ovens before dawn to the dining room at dusk — real shots from our maison. Click any frame.
            </p>
          </Reveal>
        </div>
        <Reveal>
          <div
            data-testid="gallery-grid"
            className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[150px] sm:auto-rows-[190px] lg:auto-rows-[230px] gap-3 lg:gap-4"
          >
            {GALLERY.map((g, i) => (
              <button
                key={g.src}
                data-testid={`gallery-item-${i}`}
                onClick={() => setOpen(g)}
                className={`group relative overflow-hidden rounded-2xl ${g.cls} cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C85A32]`}
              >
                <img
                  alt={g.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-[1.05]"
                  src={g.src}
                />
                <span className="absolute inset-0 bg-[#1C130E]/0 group-hover:bg-[#1C130E]/25 transition-colors duration-500" />
              </button>
            ))}
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[80] bg-[#1C130E]/90 backdrop-blur-sm grid place-items-center p-6 cursor-zoom-out"
          >
            <motion.img
              initial={{ scale: 0.94, y: 12 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 8 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              alt={open.alt}
              src={open.src}
              className="max-h-[82vh] max-w-full rounded-2xl shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]"
            />
            <button
              data-testid="gallery-lightbox-close"
              aria-label="Close image"
              className="absolute top-6 right-6 grid place-items-center w-11 h-11 rounded-full border border-[#FDFBF7]/30 text-[#FDFBF7] hover:bg-[#C85A32] hover:border-[#C85A32] transition-colors"
            >
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
